import type { ConnectedApp, OAuthConnection } from "../../db/schema";

type RuntimeOAuthConnection = Omit<
  OAuthConnection,
  | "id"
  | "clientId"
  | "clientSecret"
  | "hasClientSecret"
  | "createdAt"
  | "refreshToken"
  | "refreshTokenExpiresAt"
  | "tenantId"
>;

export type RuntimeConnector = Omit<
  ConnectedApp,
  | "name"
  | "oauthConnectionId"
  | "createdAt"
  | "createdByUserId"
  | "createdByServiceAccountId"
  | "fresh"
  | "lastTestAt"
  | "lastTestStatus"
  | "lastTestMessage"
  | "metadata"
  | "note"
  | "backingWorkflowId"
  | "tenantId"
  | "configuration"
  | "secrets"
  | "secretKeys"
> & {
  configuration: Record<string, string>;
  secrets: Record<string, string>;
  oauth?: RuntimeOAuthConnection | null;
  // The tenant API origin used as the awsRole STS token issuer, stamped by the
  // orchestrator when it loads an awsRole connector on a selfHostedSingleTenant stack.
  issuer?: string;
  principal?: string | null;
};

export type PrivateAuthApp = Pick<
  RuntimeConnector,
  "id" | "oauth" | "secrets" | "configuration"
>;

export type RouteAuthRequest = {
  method: string;
  url: string;
  routePath: string;
  headers: Record<string, string>;
};

export type RouteAuthResult =
  | { ok: true; userId?: string; email?: string }
  | { ok: false; status: number; message: string; wwwAuthenticate?: string }
  | { ok: false; redirect: string; wwwAuthenticate?: string };

type CfgFn<Cfg extends string, R> = (cfg: Record<Cfg, string>) => R;

type BaseOAuthConfig<Cfg extends string = string> = {
  grantType: CfgFn<Cfg, "authorization_code" | "client_credentials">;
  authorizationEndpoint?: CfgFn<Cfg, string>;
  tokenEndpoint: CfgFn<Cfg, string>;
  tokenEndpointAuthMethod: (
    cfg: Record<Cfg, string>,
    client: { hasSecret: boolean }
  ) =>
    | "client_secret_post"
    | "client_secret_basic"
    | "client_secret_basic_no_urlencode"
    | "none";
  authorizationParameters?: CfgFn<Cfg, Record<string, string>>;
  /** For providers whose token endpoint only parses JSON bodies, not the RFC 6749 form encoding. */
  tokenRequestFormat?: CfgFn<Cfg, "form" | "json">;
  pkce?: CfgFn<Cfg, boolean>;
  issuer?: CfgFn<Cfg, string>;
  /** RFC 8707 resource indicator, sent on authorize and token requests. */
  resource?: CfgFn<Cfg, string>;
};

export type ConnectorOAuthConfig<Cfg extends string = string> =
  | (BaseOAuthConfig<Cfg> & { clientCredentialsFrom: "env" })
  | (BaseOAuthConfig<Cfg> & {
      clientCredentialsFrom: "managed" | "db";
      scopes: CfgFn<Cfg, string[]>;
    })
  | (BaseOAuthConfig<Cfg> & {
      /** Endpoints and client come from discovery and dynamic registration at authorize time, persisted into configuration for the Base endpoint functions to read back. */
      clientCredentialsFrom: "dcr";
      discoverFrom: CfgFn<Cfg, string>;
      scopes: CfgFn<Cfg, string[]>;
    });

export type FieldTarget =
  | { kind: "connectedApp"; field: "name" | "urlPattern" }
  | { kind: "configuration"; field: string }
  | { kind: "secrets"; field: string }
  | { kind: "oauthConnection"; field: "clientId" | "clientSecret" | "scopes" };

export type VisibleWhen = {
  target: FieldTarget;
  /** When set, field is visible only if the target equals this value. When omitted, visible if the target is non-empty. */
  value?: string;
  /** When set, field is visible only if the target does not equal this value. */
  notValue?: string;
};

export type InputField = {
  kind: "input";
  label: string;
  target: FieldTarget;
  placeholder?: string;
  required?: boolean;
  helperText?: string;
  defaultValue?: string;
  visibleWhen?: VisibleWhen;
};

export type UrlField = {
  kind: "url";
  label: string;
  target: FieldTarget;
  placeholder?: string;
  required?: boolean;
  helperText?: string;
  defaultValue?: string;
  visibleWhen?: VisibleWhen;
};

export type HostnameField = {
  kind: "hostname";
  label: string;
  target: FieldTarget;
  placeholder?: string;
  required?: boolean;
  helperText?: string;
  defaultValue?: string;
  visibleWhen?: VisibleWhen;
};

export type SelectField = {
  kind: "select";
  label: string;
  target: FieldTarget;
  options: Array<{ value: string; label: string }>;
  required?: boolean;
  helperText?: string;
  defaultValue?: string;
  visibleWhen?: VisibleWhen;
};

export type MarkdownItem = {
  kind: "markdown";
  content: string | ((configuration: Record<string, string>) => string);
};

export type CopyableItem = {
  kind: "copyable";
  label: string;
  value: string;
};

export type OAuthItem = {
  kind: "oauth";
  dependsOn?: FieldTarget[];
  visibleWhen?: VisibleWhen;
};

// One-click GitHub App registration and install via the app-manifest flow.
export type GithubAppManifestItem = {
  kind: "githubAppManifest";
};

export type FileField = {
  kind: "file";
  label: string;
  target: FieldTarget;
  accept?: string;
  helperText?: string;
};

export type UrlPatternField = {
  kind: "url-pattern";
  target: FieldTarget;
  placeholder?: string;
};

export type TestUrlField = {
  kind: "test-url";
  label: string;
  urlTarget: FieldTarget;
  methodTarget: FieldTarget;
  placeholder?: string;
  helperText?: string;
};

// No `target`: setWorkflowAppBacking handles all derived writes atomically.
export type WorkflowPickerField = {
  kind: "workflow-picker";
  label: string;
  helperText?: string;
};

// Read-only list of the backing workflow's API routes with their exposure state.
export type ExposedRoutesField = {
  kind: "exposed-routes";
  label: string;
  helperText?: string;
};

export type ListFieldColumn = {
  field: string;
  placeholder?: string;
  /** Shown instead of `placeholder` while the cell is stored encrypted. */
  secretPlaceholder?: string;
  /**
   * Stored encrypted under `secrets` by default, with a per-row toggle to
   * store in `configuration` instead; which record holds the value is the flag.
   */
  secret?: boolean;
};

export type ListFieldPrimaryCell = {
  target: FieldTarget;
  placeholder?: string;
};

// Populates a list field's pickable catalog from the provider: `request` builds
// the endpoint URL (auth is injected by the proxy on egress), `extract` maps the
// response body to rows keyed by column field. The catalog is what the user
// picks from; only picks become allowlist rows.
export type ListFieldFetch = {
  label: string;
  request: (configuration: Record<string, string>) => string;
  extract: (body: string) => Array<Record<string, string>>;
};

export type ListField = {
  kind: "list";
  label: string;
  /** Rows persist as flat keys: `${prefix}.${rowId}.${column}`. */
  prefix: string;
  columns: ListFieldColumn[];
  /**
   * Pinned, non-removable first row whose cells write to explicit targets
   * (keyed by column field) — the primary credential in its legacy fields.
   */
  primaryRow?: Record<string, ListFieldPrimaryCell>;
  addLabel: string;
  fetch?: ListFieldFetch;
  helperText?: string;
  required?: boolean;
  visibleWhen?: VisibleWhen;
};

export type StepItem =
  | InputField
  | UrlField
  | HostnameField
  | SelectField
  | MarkdownItem
  | CopyableItem
  | OAuthItem
  | GithubAppManifestItem
  | FileField
  | UrlPatternField
  | TestUrlField
  | ListField
  | WorkflowPickerField
  | ExposedRoutesField;

type Step = {
  title: string;
  items: StepItem[];
};

import type { AuthContext } from "./authContext";
import type { AuthDescriptor } from "./authDescriptor";
import type { AppTag } from "./tags";
import { urlPatternError } from "./urlPatternConversion";

export type AuthBuilderInput<
  Cfg extends string = string,
  Sec extends string = string,
> = AuthContext<Cfg, Sec> & PrivateAuthApp;

type TestRequestConfig = {
  url: string;
  method?: "GET" | "POST";
  headers?: Record<string, string>;
  body?: string;
  expectedStatus?: number | number[];
  expectedBodyContains?: string | string[];
};

export type ConnectionTest<Cfg extends string = string> =
  | { protocol: "http"; request: CfgFn<Cfg, string | TestRequestConfig> }
  | { protocol: "postgres"; connectionString: CfgFn<Cfg, string> }
  | { protocol: "mongodb"; connectionString: CfgFn<Cfg, string> }
  | { protocol: "mysql"; connectionString: CfgFn<Cfg, string> }
  | { protocol: "mssql"; connectionString: CfgFn<Cfg, string> }
  | { protocol: "smtp"; connectionString: CfgFn<Cfg, string> }
  | { protocol: "ldap"; connectionString: CfgFn<Cfg, string> }
  | { protocol: "imap"; connectionString: CfgFn<Cfg, string> };

type ConnectionTestUrl<R> = R extends string
  ? R
  : R extends { url: infer U extends string }
    ? U
    : never;

type HasUnsubstitutedPlaceholder<S extends string> =
  S extends `${string}{${string}}${string}` ? true : false;

type UnsubstitutedPlaceholderError = {
  "🛑 connection-test url has an unsubstituted {placeholder} — take the config key as an argument and interpolate it, e.g. ({ serverUrl }) => `${serverUrl}/path`": never;
};

type RejectPlaceholderUrl<R> =
  HasUnsubstitutedPlaceholder<ConnectionTestUrl<R>> extends true
    ? UnsubstitutedPlaceholderError
    : unknown;

type ValidatedConnectionTest<
  Cfg extends string,
  HttpReturn extends string | TestRequestConfig,
> =
  | {
      protocol: "http";
      request: (
        cfg: Record<Cfg, string>
      ) => HttpReturn & RejectPlaceholderUrl<HttpReturn>;
    }
  | Exclude<ConnectionTest<Cfg>, { protocol: "http" }>;

/**
 * Static descriptor, or a function that picks one based on the connector’s runtime
 * config. Use the function form when the user’s connect-form input determines
 * which descriptor shape to use
 */
export type AuthSpec =
  | AuthDescriptor
  | ((app: AuthBuilderInput) => AuthDescriptor);

export type MeteredMeasure = {
  measure: string;
  /** Priced per model; renders as a column in the per-model grid rather than a flat row. */
  perModel?: boolean;
};

export type ConnectorUsage = {
  /** The billable measures this connector meters, in display order — the source of truth for which pricing and limit inputs appear. */
  measures: readonly MeteredMeasure[];
};

export type ConnectorDefinition<Identifier extends string = string> = {
  identifier: Identifier;
  name: string;
  tagLine: string;
  keywords?: string;
  aiContext?: string;
  logo: string;
  tags: AppTag[];
  /**
   * How this variant authenticates (e.g. “REST API key”, “User OAuth”),
   * shown as its option in the connection-type picker. Set by defineProduct.
   */
  authLabel?: string;
  defaultUrlPattern: (configuration: Record<string, string>) => string;
  auth: AuthSpec;
  protocol: string;
  connectionTest?: ConnectionTest;
  steps: Step[];
  /** When true, skip auto-appending the "Customize this connector" step (for apps that place name/URL inline). */
  customizeStepHandledInline?: boolean;
  oauth?: ConnectorOAuthConfig;
  /** Returns environment variables this app provides at runtime */
  publicEnvVars?: (
    configuration: Record<string, string>
  ) => Record<string, string>;
  /** Short usage note returned to the AI after connectToApp */
  usageHint?: (configuration: Record<string, string>) => string | undefined;
  /** How this connector's billable usage is measured; its presence surfaces the Measure pricing and Usage limits sections. Transport activity is recorded for every connector regardless. */
  usage?: ConnectorUsage;
};

/**
 * One connector type as the UI presents it, holding a definition per way of
 * connecting. The type is itself the default definition, and `variants[0]` —
 * a cycle, so never JSON-serialize a definition.
 */
export type ConnectorType<Identifier extends string = string> =
  ConnectorDefinition<Identifier> & {
    variants: readonly ConnectorDefinition[];
  };

type StepConstraint = { title: string; items: readonly StepItem[] };

type FieldsAt<Item, Key extends string, Kind extends string> = Item extends {
  [_ in Key]: { kind: Kind; field: infer F extends string };
}
  ? F
  : never;

type FieldsIn<Steps extends readonly StepConstraint[], Kind extends string> =
  | FieldsAt<Steps[number]["items"][number], "target", Kind>
  | FieldsAt<Steps[number]["items"][number], "urlTarget", Kind>
  | FieldsAt<Steps[number]["items"][number], "methodTarget", Kind>;

export type ConfigFields<S extends readonly StepConstraint[]> = FieldsIn<
  S,
  "configuration"
>;
export type SecretFields<S extends readonly StepConstraint[]> = FieldsIn<
  S,
  "secrets"
>;

type AuthCallback<Cfg extends string, Sec extends string> =
  | AuthDescriptor
  | ((app: AuthBuilderInput<Cfg, Sec>) => AuthDescriptor);

// Cfg/Sec are defaulted, so `ConfigFields<Steps>` and `SecretFields<Steps>`
// are each instantiated exactly once per manifest rather than once per
// callback slot.
type TypedManifest<
  Identifier extends string,
  Steps extends readonly StepConstraint[],
  Cfg extends string = ConfigFields<Steps>,
  Sec extends string = SecretFields<Steps>,
> = {
  identifier: Identifier;
  name: string;
  tagLine: string;
  keywords?: string;
  aiContext?: string;
  logo: string;
  tags: AppTag[];
  protocol: string;
  connectionTest?: ConnectionTest<Cfg>;
  steps: Steps;
  customizeStepHandledInline?: boolean;
  auth: AuthCallback<Cfg, Sec>;
  oauth?: ConnectorOAuthConfig<Cfg>;
  defaultUrlPattern: CfgFn<Cfg, string>;
  publicEnvVars?: CfgFn<Cfg, Record<string, string>>;
  usageHint?: CfgFn<Cfg, string | undefined>;
  usage?: ConnectorUsage;
};

type ProductVariant<
  Identifier extends string,
  Steps extends readonly StepConstraint[],
  Cfg extends string = ConfigFields<Steps>,
  Sec extends string = SecretFields<Steps>,
> = Omit<
  TypedManifest<Identifier, Steps, Cfg, Sec>,
  "name" | "tagLine" | "logo" | "tags"
> & {
  /** How this variant authenticates — its option in the connection-type picker. */
  authLabel: string;
};

export function productVariant<
  const Identifier extends string,
  const Steps extends readonly StepConstraint[],
  const HttpTestReturn extends string | TestRequestConfig =
    | string
    | TestRequestConfig,
>(
  variant: Omit<ProductVariant<Identifier, Steps>, "connectionTest"> & {
    connectionTest?: ValidatedConnectionTest<
      ConfigFields<Steps>,
      HttpTestReturn
    >;
  }
): ProductVariant<Identifier, Steps> {
  return variant as ProductVariant<Identifier, Steps>;
}

// A product authored as one unit: one identity (name, tagline, logo, tags)
// offering one definition per way of connecting. The first variant is the
// type's own definition, and its identifier names the type.
export function defineProduct<
  Variants extends readonly ProductVariant<
    string,
    readonly StepConstraint[],
    string,
    string
  >[],
>(product: {
  name: string;
  tagLine: string;
  logo: string;
  tags: AppTag[];
  variants: readonly [...Variants];
}): ConnectorType<Variants[0]["identifier"]> {
  const { variants } = product;
  const labels = new Set(variants.map((v) => v.authLabel));
  if (labels.size !== variants.length) {
    throw new Error(
      `${product.name}: product variants must declare distinct authLabels`
    );
  }
  const definitions = variants.map((variant) => ({
    ...buildDefinition({
      ...variant,
      name: product.name,
      tagLine: product.tagLine,
      logo: product.logo,
      tags: product.tags,
    } as TypedManifest<string, readonly StepConstraint[], string, string>),
    authLabel: variant.authLabel,
  }));
  const type = definitions[0]! as ConnectorType<Variants[0]["identifier"]>;
  type.variants = definitions;
  return type;
}

export function defineConnector<
  const Identifier extends string,
  const Steps extends readonly StepConstraint[],
  const HttpTestReturn extends string | TestRequestConfig =
    | string
    | TestRequestConfig,
>(
  definition: Omit<TypedManifest<Identifier, Steps>, "connectionTest"> & {
    connectionTest?: ValidatedConnectionTest<
      ConfigFields<Steps>,
      HttpTestReturn
    >;
  }
): ConnectorType<Identifier> {
  const type = buildDefinition(definition) as ConnectorType<Identifier>;
  type.variants = [type];
  return type;
}

function buildDefinition<
  const Identifier extends string,
  const Steps extends readonly StepConstraint[],
>(
  definition: TypedManifest<Identifier, Steps>
): ConnectorDefinition<Identifier> {
  const rawDefaultUrlPattern = definition.defaultUrlPattern;
  const built: TypedManifest<Identifier, Steps> = {
    ...definition,
    defaultUrlPattern: (configuration) => {
      const pattern = rawDefaultUrlPattern(configuration);
      const error = urlPatternError(pattern);
      if (error) {
        throw new Error(
          `${definition.identifier} defaultUrlPattern: ${error} (${pattern})`
        );
      }
      return pattern;
    },
  };

  if (built.customizeStepHandledInline) {
    return built as unknown as ConnectorDefinition<Identifier>;
  }

  const customizeStep: Step = {
    title: "Customize this connector",
    items: [
      {
        kind: "input",
        label: "Name",
        required: true,
        defaultValue: built.name,
        target: { kind: "connectedApp", field: "name" },
        helperText: "How this connector is displayed in 3B.",
      },
      {
        kind: "url-pattern",
        target: { kind: "connectedApp", field: "urlPattern" },
      },
    ],
  };

  if (built.protocol === "http") {
    customizeStep.items.push({
      kind: "file",
      label: "CA certificate",
      accept: ".pem,.crt,.cer",
      helperText:
        "Optional. Upload a PEM CA certificate to trust a private or self-signed server.",
      target: { kind: "configuration", field: "caCertificate" },
    });
  }

  const steps = [...built.steps] as Step[];
  const lastStep = steps[steps.length - 1];
  const lastStepHasOAuth = lastStep?.items.some(
    (item) => item.kind === "oauth"
  );
  if (lastStepHasOAuth) {
    steps.splice(steps.length - 1, 0, customizeStep);
  } else {
    steps.push(customizeStep);
  }

  return {
    ...built,
    steps,
  } as unknown as ConnectorDefinition<Identifier>;
}
